import { SofReceivingOrder } from '../../../models/sof-receiving/SofReceivingOrder.interface';
import { orderDate } from './parse-sof-date';

/** Descending compare on `sof`, numeric when both sides parse as numbers. */
function compareSofDescending(a: string, b: string): number {
  const aNum = Number(a);
  const bNum = Number(b);
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
    return bNum - aNum;
  }
  return b.localeCompare(a);
}

/**
 * Sorts the queue by delivery date, SOONEST FIRST — the most urgent pick at the
 * top — with a stable tiebreak on `sof` descending so equal timestamps still
 * render deterministically. History gets the same ordering.
 *
 * Ordering reads `dateRequired` via `orderDate`, NOT `dateSort`. Both derive
 * from the same `SOF.Orders.DeliveryDate`, but the view formats `dateSort` as
 * `yyyymmdd` and throws the time away, so sorting on it cannot order two
 * deliveries on the same day. `dateSort` remains the fallback.
 *
 * Orders with no usable date sort LAST rather than being treated as the
 * earliest — a missing date is unknown urgency, not maximum urgency, and
 * floating it to the top of a pick queue would be actively misleading.
 *
 * Pure — returns a new array; does not mutate the input.
 *
 * @param orders - The queue rows (already deduped to one per SOF).
 * @returns A new array, soonest delivery date first.
 */
export function sortOrdersSoonestFirst(
  orders: SofReceivingOrder[]
): SofReceivingOrder[] {
  return [...orders].sort((a, b) => {
    const aDate = orderDate(a);
    const bDate = orderDate(b);

    if (aDate === null && bDate === null) {
      return compareSofDescending(a.sof, b.sof);
    }
    if (aDate === null) {
      return 1;
    }
    if (bDate === null) {
      return -1;
    }

    const diff = aDate.getTime() - bDate.getTime();
    return diff !== 0 ? diff : compareSofDescending(a.sof, b.sof);
  });
}
